import React, { useContext, useRef, useState } from "react";
import { AppBar, Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import Logo from "../../assets/images/logo.png";
import EmbedContext from "../../context/Embed";
import { useSearch } from "../../context/Search";
import MobileNavDrawer from "../SideNav/MobileNavDrawer";
import BecomePartnerLink from "./BecomePartnerLink";
import DeliveryPickupToggle from "./DeliveryPickupToggle";
import HeaderSearchBar from "./HeaderSearchBar";
import LocationTimePill from "./LocationTimePill";
import SearchDropdown from "./SearchDropdown";
import { C, FONT_BODY, FONT_DISPLAY, FOCUS_RING, RADIUS, useTopBarHeight } from "../../utils/designTokens";

const CATERING_SEARCH = "Search caterers, cuisines or dishes";
const SEARCH_PLACEHOLDER = "Search restaurants and dishes";

/** Logo mark + the RushMyFood wordmark. */
function Brand({ to, showWordmark }) {
  const inner = (
    <>
      <Box component="img" src={Logo} alt="" sx={{ width: 40, height: 40, objectFit: "contain", flexShrink: 0 }} />
      {showWordmark && (
        <Box
          component="span"
          sx={{
            fontFamily: FONT_DISPLAY,
            fontWeight: 800,
            fontSize: 21,
            letterSpacing: "-0.02em",
            color: C.ink,
            whiteSpace: "nowrap",
          }}
        >
          RushMyFood
        </Box>
      )}
    </>
  );
  const sx = { display: "inline-flex", alignItems: "center", gap: 1, flexShrink: 0, textDecoration: "none", borderRadius: RADIUS.control, ...FOCUS_RING };
  if (!to) return <Box sx={sx} aria-label="RushMyFood">{inner}</Box>;
  return (
    <Box component={RouterLink} to={to} aria-label="RushMyFood home" sx={sx}>
      {inner}
    </Box>
  );
}

/** Red pill with the bag and how many items are in it. */
function CartPill({ to, count }) {
  return (
    <Box
      component={RouterLink}
      to={to}
      aria-label={`Cart, ${count} ${count === 1 ? "item" : "items"}`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        height: 44,
        px: 2,
        flexShrink: 0,
        borderRadius: RADIUS.pill,
        bgcolor: C.red,
        color: "#FFFFFF",
        textDecoration: "none",
        fontFamily: FONT_BODY,
        fontWeight: 700,
        fontSize: 15,
        fontVariantNumeric: "tabular-nums",
        transition: "background-color .15s ease",
        "&:hover": { bgcolor: C.redHover },
        ...FOCUS_RING,
      }}
    >
      <ShoppingBagOutlinedIcon sx={{ fontSize: 20 }} />
      <span>{count}</span>
    </Box>
  );
}

/**
 * The full top bar, shared by the signed-in and signed-out headers. It is
 * fixed, white, with a hairline under it, and publishes its own height as
 * --rmf-topbar-h so pages can stick their bars right below it.
 *
 *   account — the right-hand account control (avatar menu, or Sign in)
 *   showNav — the phone hamburger and its nav drawer (signed-in only)
 */
function TopBar({ account, cartCount = 0, cartHref = "/checkout", showCart = true, showNav = false, logoTo = "/restaurant-list" }) {
  const theme = useTheme();
  // Below md the bar wraps to two rows (pages there already leave room for a
  // second header row); below lg it drops the wordmark and long labels.
  const phone = useMediaQuery(theme.breakpoints.down("md"));
  const tablet = useMediaQuery(theme.breakpoints.down("lg"));
  const tabletRow = useMediaQuery(theme.breakpoints.up("sm"));
  // Room for the Catering pill's label.
  const wide = useMediaQuery("(min-width:1280px)");
  const location = useLocation();
  const { isEmbed } = useContext(EmbedContext);
  const { search: searchValue, setSearch } = useSearch();
  const [navOpen, setNavOpen] = useState(false);
  const barRef = useRef(null);
  useTopBarHeight(barRef);

  const path = location.pathname;
  // /pickup is the same list in pickup mode.
  const isRestaurantsRoute = path === "/" || path.startsWith("/restaurant-list") || path.startsWith("/pickup");
  const isCateringRoute = path.startsWith("/catering");
  const isStorePage = path.startsWith("/restaurant/");
  const handleSearchChange = (e) => setSearch(e.target.value);

  // Embed mode (a partner's iframe pinned to one restaurant): nothing here may
  // lead away from that restaurant, so no search, no catering, no logo link.
  const showSearch = !isEmbed;
  const search = isCateringRoute ? (
    <HeaderSearchBar value={searchValue} onChange={handleSearchChange} placeholder={CATERING_SEARCH} />
  ) : (
    <SearchDropdown
      value={searchValue}
      onChange={handleSearchChange}
      placeholder={SEARCH_PLACEHOLDER}
      deferQuery={!isRestaurantsRoute}
    />
  );

  return (
    <AppBar
      ref={barRef}
      elevation={0}
      position="fixed"
      sx={{
        bgcolor: `${C.bg} !important`,
        color: C.ink,
        borderBottom: `1px solid ${C.border}`,
        // Clip sideways overflow only; the search panel portals out anyway.
        overflowX: "clip",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, md: 1.5 },
          minHeight: 64,
          px: { xs: 1.5, sm: 2, md: 3 },
          py: "10px",
          boxSizing: "border-box",
          width: "100%",
          maxWidth: 1600,
          mx: "auto",
        }}
      >
        {phone && showNav && !isEmbed && (
          <IconButton
            onClick={() => setNavOpen(true)}
            aria-label="Open menu"
            sx={{ width: 44, height: 44, ml: -0.5, color: C.ink, ...FOCUS_RING }}
          >
            <MenuRoundedIcon />
          </IconButton>
        )}

        <Brand to={isEmbed ? null : logoTo} showWordmark={!tablet || phone} />


        {!phone && (
          <>
            <DeliveryPickupToggle compact={tablet} />
            <LocationTimePill maxLabelWidth={tablet ? 96 : 140} />
          </>
        )}

        {!phone && showSearch ? (
          <Box sx={{ display: "flex", flex: 1, minWidth: 120, maxWidth: 560, mx: { md: 0.5 } }}>
            {search}
          </Box>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, ml: "auto" }}>
          {!phone && <BecomePartnerLink compact={!wide} />}
          {account}
          {showCart && <CartPill to={cartHref} count={cartCount} />}
        </Box>
      </Box>

      {/* Phones wrap: the store page puts mode + place + time on a second row;
          the discovery screens put their search there instead. An embed keeps
          this row: mode, place and time never lead away from the restaurant. */}
      {phone && isStorePage && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: { xs: 1.5, sm: 2 },
            pb: 1,
            overflowX: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <DeliveryPickupToggle compact />
          <LocationTimePill maxLabelWidth={120} compact />
        </Box>
      )}
      {phone && showSearch && (isRestaurantsRoute || isCateringRoute) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: { xs: 1.5, sm: 2 }, pb: 1 }}>
          {/* Tablets have room for mode + place beside the search. */}
          {tabletRow && (
            <>
              <DeliveryPickupToggle compact />
              <LocationTimePill maxLabelWidth={110} compact />
            </>
          )}
          <Box sx={{ display: "flex", flex: 1, minWidth: 0 }}>{search}</Box>
        </Box>
      )}

      {phone && showNav && !isEmbed && (
        <MobileNavDrawer open={navOpen} onClose={() => setNavOpen(false)} />
      )}
    </AppBar>
  );
}

export default React.memo(TopBar);
